<template>
  <div class="form">
    <div class="form__content">
      <IdentityQueueStepCounter />
      <h1 class="extra-large-headline">
        {{ $t("PageTitle.headline.selfiePageHeader") }}
      </h1>
      <p class="bodycopy">
        {{ $t("PageSubTitle.paragraph.selfieInstructions") }}
      </p>
      <ul class="form__selfie-instructions bodycopy">
        <li v-for="list in bodyCopy" :key="list">
          {{ list }}
        </li>
      </ul>
    </div>
    <form @submit.prevent>
      <FileUploadField
        name="selfieDocument"
        iconType="VerificationCircle"
        :errors="form.selfieDocumentErrors"
        :label="$t('FileUploadField.label.selfieDocument')"
        :placeholder="$t('FileUploadField.placeholder.selectOrDrag')"
        @updateFile="updateFiles"
      />

      <ButtonBase
        :isDisabled="formHasErrors || globalStore.getIsFormLoading"
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        {{ $t("ButtonBase.label.selfieClear") }}
      </ButtonBase>
      <IdentityQueueNext />
    </form>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import { useGlobalStore } from "@/stores/globalStore";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";

import useApiHelpers from "@/composables/api/helpers";
import useCustomerActions from "@/composables/api/useCustomerActions";

import FileUploadField from "@/components/ui/inputs/FileUploadField.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";
import useFormValidation from "@/composables/validations/useFormValidation";
import IdentityQueueStepCounter from "@/components/layout/progress/IdentityQueueStepCounter.vue";
import IdentityQueueNext from "@/components/layout/progress/IdentityQueueNext.vue";

const route = useRoute();
const router = useRouter();
const globalStore = useGlobalStore();
const identityQueueStore = useIdentityQueueStore();

const { validateFileInput } = useFormValidation();
const { postRequest } = useApiHelpers();
const { fetchCustomerId } = useCustomerActions();
const { t } = useI18n();

const form = ref({
  selfieDocument: [],
  selfieDocumentErrors: "",
});

const updateFiles = (file) => {
  const fileValidationErrors = validateFileInput("selfieDocument", file);
  form.value.selfieDocument = file;

  form.value.selfieDocumentErrors = fileValidationErrors;
};

const handleFormSubmit = async () => {
  const custId = await fetchCustomerId();
  const formData = new FormData();
  const docSubType = "";
  const docType = "selfie";

  formData.set("documentFile", form.value.selfieDocument);
  formData.set("documentType", docType);
  formData.set("documentSubType", docSubType);
  formData.set("documentReference", custId);

  globalStore.setIsFormLoading(true);
  postRequest("documents", formData, () => {
    identityQueueStore.updateToNextRoute();
    identityQueueStore.fetchNextIdentityRoute(route).then((nextRoute) => {
      router.push(nextRoute);
    });
  });
};

const formHasErrors = computed(() => {
  const anyErrors = form.value.selfieDocumentErrors !== "";
  const anyFieldsEmpty = form.value.selfieDocument.length === 0;
  return anyErrors || anyFieldsEmpty;
});

const bodyCopy = computed(() =>
  t("BodyCopy.paragraph.selfieInstructions").split("','")
);
</script>
<style lang="scss" scoped>
.form {
  .bodycopy {
    margin-bottom: 1rem;
    @include breakpoint($min-m) {
      margin-bottom: 1.5rem;
    }
  }
  &__selfie-instructions {
    list-style: unset;
    padding-inline: 1.5rem;

    li {
      font-size: $font-body;
      line-height: 1.5;
    }
  }
}
</style>
